import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { formatDistanceToNow } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Format date as relative time (e.g., "2 hours ago")
export function formatRelativeDate(dateString: string): string {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch {
    return dateString;
  }
}

// Format date as absolute date with configurable format
export type DateFormat = 'short' | 'medium' | 'long' | 'datetime';

export function formatDate(
  dateString: string | null,
  format: DateFormat = 'medium'
): string {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  
  switch (format) {
    case 'short':
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    case 'medium':
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    case 'long':
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    case 'datetime':
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
  }
}

// Convert snake_case to Title Case
export function formatSnakeCaseToTitle(text: string): string {
  return text
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Generate URL-friendly slug from text
export function generateSlug(text: string, separator: string = '-'): string {
  return text
    .toLowerCase()
    .replace(/\s+/g, separator)
    .replace(new RegExp(`[^a-z0-9${separator}]`, 'g'), '');
}
